/**
 * cardStepPrice.ts — the ONE monthly figure the card step prints, and the rule
 * that decides which server it comes from.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY THIS EXISTS (review of the merged delivery-and-payment step, 2026-09-23).
 * The card screen states the plan's monthly price, its term total, the amount
 * of the month-1 hold (the rail's "Held on your card today" headline since
 * 2026-09-26; it was `CardHoldExplainer`'s first point before) or the payment
 * taken today, and it
 * reports that same amount to the funnel (`add_payment_info`). All four must be
 * the figure orders-service will actually hold or capture — and that figure
 * does not always come from the same place. It depends on whether orders still
 * has to PRICE the order or has already FIXED the price (orders-service `dev`,
 * `orders.service.ts`, checked 2026-09-23):
 *
 *   - `'ladder'` — orders re-quotes at session mint. A `plan_selected` draft is
 *     upgraded to `awaiting_payment` with `prices.getPrice(productKey, months)`,
 *     and a fresh checkout is created with the same call, so the hold is the
 *     CURRENT prices-service rung. The plan-options ladder the safety gate
 *     already reads comes from that same matrix, so it is the true figure here —
 *     and the quote the patient chose at (orders' selection echo, or a resumed
 *     draft's stored `monthlyPence`) can be STALE after a reprice. That was the
 *     defect: a resumed draft printed the old monthly while orders held the new.
 *   - `'orders'` — orders has already fixed the price and will NOT re-quote.
 *     Two lanes: the clinician-led (preapproved) plan, whose month 1 is captured
 *     at the case's stored `monthlyPence` (`r2a.monthlyPence`), which is exactly
 *     what plan-preview echoed; and a resumed `awaiting_payment` session, whose
 *     hold is the `monthlyPence` stored at its FIRST session mint (the existing-
 *     case branch keeps the plan "authoritative from first initialise"), which is
 *     exactly what checkout/resume echoed. Printing the ladder on these lanes
 *     would recreate the same defect pointing the other way.
 *
 * ⚠️ NEVER `monthlyPence × months` HERE OR AT A CALL SITE. The term total is the
 * ladder rung's `termPricing(...).actualPence` — the same function and the same
 * field PlanSelection prints on the plan step, so both screens state a total the
 * same way. On the `'orders'` lane that rung is only used when it AGREES with the
 * fixed monthly; when the catalogue has moved since, the total is DROPPED rather
 * than invented (a missing total is recoverable, two disagreeing amounts on the
 * card screen are not).
 *
 * Null means "print no money": the plan-options read is still in flight or
 * failed (the pay control is blocked then anyway — no content, no sale), the
 * catalogue does not price this term, or it is the 0-pence launch placeholder,
 * which must never print "£0".
 */
import type { PricePlan } from '../api/types';
import { termPricing } from '../lib/termPricing';

/** Which server's figure the card step prints — see the module header. */
export type CardStepQuoteSource = 'ladder' | 'orders';

export interface CardStepPrice {
  /** What orders will hold (hold route) or capture (clinician-led) for month 1, and bill each month. */
  monthlyPence: number;
  /** The rung's whole-term total, or null when there is no server-priced total to show. */
  termTotalPence: number | null;
  /**
   * The monthly the patient CHOSE at, when the live price differs from it —
   * the card screen must say so rather than silently print a different number.
   * Only ever set on the `'ladder'` lane, and never from a 0 (a quote of 0 is
   * the launch placeholder or an absent field, not a price anyone was shown).
   */
  changedFromPence: number | null;
}

export function cardStepPrice(input: {
  choice: { months: number; monthlyPence: number } | null;
  /** The chosen product's plan-options ladder; null while loading or after a failed read. */
  ladder: PricePlan[] | null;
  source: CardStepQuoteSource;
}): CardStepPrice | null {
  const { choice, ladder, source } = input;
  // No ladder in hand ⇒ no money rows at all, on BOTH lanes: while it loads the
  // only figure available on the 'ladder' lane is the possibly-stale quote, and
  // the pay control is gated on the same read, so waiting costs nothing.
  if (!choice || !ladder) return null;
  const rung = termPricing(ladder).find((p) => p.months === choice.months) ?? null;

  if (source === 'orders') {
    if (!(choice.monthlyPence > 0)) return null;
    return {
      monthlyPence: choice.monthlyPence,
      termTotalPence: rung && rung.monthlyPence === choice.monthlyPence ? rung.actualPence : null,
      changedFromPence: null,
    };
  }

  if (!rung || !(rung.monthlyPence > 0)) return null;
  return {
    monthlyPence: rung.monthlyPence,
    termTotalPence: rung.actualPence,
    changedFromPence:
      choice.monthlyPence > 0 && choice.monthlyPence !== rung.monthlyPence ? choice.monthlyPence : null,
  };
}
